import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react'
import { Button, Checkbox, Segment, Grid, Dropdown, Modal, Form } from 'semantic-ui-react'
import * as R from 'ramda'
import styled from 'styled-components'

import ConditionalSetting from './ConditionalSetting'
import { ConfirmDeletePopup } from 'components/ConfirmDeletePopup'
import Popup from 'shared/components/Popup'
import { AllowPermission } from 'shared/components/AllowPermission'
import constants from 'config/constants'
import useFeatureToggle from 'hooks/useFeatureToggle'
import { LinkButton } from 'elements/LinkButton'
import usePlanFeature from 'hooks/usePlanFeature'
import { getQuestionChoiceDropdownOptions } from 'organisms/QuestionConditionalLogic/utils'

const isMissingFollowUp = (followUp) => R.isNil(followUp) || R.isEmpty(followUp)

export default function QuestionConditionModal({
  question,
  onSubmit,
  isOpen,
  close,
  shouldShowChoiceValueOnRule,
  isLogicLoading,
}: QuestionConditionModalPropType) {
  const [questionCondition, setQuestionCondition] = useState<QuestionType>(question)
  const [deletedCondition, setdeletedConditions] = useState<Condition[]>([])

  const [ruleOptions, setRuleOptions] = useState(getQuestionChoiceDropdownOptions(question))
  const [selectedRuleOptions, setSelectedRuleOptions] = useState([])
  const [isTriggerSubmit, setIsTriggerSubmit] = useState(false)

  const { enableFileUploadQuestion } = useFeatureToggle()
  const isFileUploadPlanEnabled = usePlanFeature('file_upload', !enableFileUploadQuestion)

  const hiddenSubmitButtonRef = useRef(null)

  const setForm = useCallback((path: R.Path, value) => {
    setQuestionCondition((state) => R.assocPath(path, value, state))
  }, [])

  const choiceSentimentMapper = useMemo<MapperType>(
    () => calculateChoiceSentimentMapper(question),
    [question]
  )

  const isPositiveChoice = useCallback(
    (choiceId) => R.includes(choiceId, choiceSentimentMapper['positive']),
    [choiceSentimentMapper]
  )

  const isNeutralChoice = useCallback(
    (choiceId) => R.includes(choiceId, choiceSentimentMapper['neutral']),
    [choiceSentimentMapper]
  )

  const isNegativeChoice = useCallback(
    (choiceId) => R.includes(choiceId, choiceSentimentMapper['negative']),
    [choiceSentimentMapper]
  )

  useEffect(() => {
    if (question) {
      const newConditions = question.conditions.map((condition) => {
        condition.conditional_question_type =
          condition.conditional_question_type || 'follow_up_comment_question'

        if (isMissingFollowUp(condition.follow_up_comment_question)) {
          condition.follow_up_comment_question = DEFEAUT_CONDITION.follow_up_comment_question
        }

        if (isMissingFollowUp(condition.follow_up_file_upload_question)) {
          condition.follow_up_file_upload_question =
            DEFEAUT_CONDITION.follow_up_file_upload_question
        }

        if (isMissingFollowUp(condition.follow_up_multiple_choice_question)) {
          condition.follow_up_multiple_choice_question =
            DEFEAUT_CONDITION.follow_up_multiple_choice_question
        }

        if (isMissingFollowUp(condition.follow_up_multi_select_choice_question)) {
          condition.follow_up_multi_select_choice_question =
            DEFEAUT_CONDITION.follow_up_multi_select_choice_question
        }

        return condition
      })
      setQuestionCondition({ ...question, conditions: newConditions })
    }
  }, [question])

  useEffect(() => {
    if (R.isEmpty(R.prop('conditions', question))) {
      setForm(['conditions'], [DEFEAUT_CONDITION])
      setForm(['is_active'], true)
    }
  }, [question, setForm])

  useEffect(() => {
    if (questionCondition) {
      let selectedRule = []
      questionCondition.conditions.forEach((condition) => {
        selectedRule = [...selectedRule, ...condition.rules]
      })
      setSelectedRuleOptions(selectedRule)
    }
  }, [questionCondition])

  useEffect(() => {
    if (selectedRuleOptions) {
      const initialRuleOptions = getQuestionChoiceDropdownOptions(question)
      const newOptions = initialRuleOptions.filter((option) => {
        // exclude sentiment option
        if (R.any(isPositiveChoice, selectedRuleOptions) && option.value === 'positive') {
          return false
        }

        if (R.any(isNeutralChoice, selectedRuleOptions) && option.value === 'neutral') {
          return false
        }

        if (R.any(isNegativeChoice, selectedRuleOptions) && option.value === 'negative') {
          return false
        }

        // exclude choice ids
        if (R.includes('positive', selectedRuleOptions) && isPositiveChoice(option.value)) {
          return false
        }

        if (R.includes('neutral', selectedRuleOptions) && isNeutralChoice(option.value)) {
          return false
        }

        if (R.includes('negative', selectedRuleOptions) && isNegativeChoice(option.value)) {
          return false
        }

        return true
      })
      setRuleOptions(newOptions)
    }
  }, [selectedRuleOptions, question, isPositiveChoice, isNeutralChoice, isNegativeChoice])

  function getLabel(option) {
    if (R.includes(option.value, ['positive', 'neutral', 'negative'])) {
      return {
        content: <span className={`bold text-${iconText[option.value]}`}>{option.text}</span>,
        color: sentimentLabelColor[option.sentiment],
      }
    } else if (R.path(['choice', 'sentiment'], option)) {
      const sentiment = option.choice.sentiment
      const isStarType = question.type === 'star'
      if (isStarType) {
        const showMinMaxLabel = option.choice.value == 1 || option.choice.value == 5
        return {
          content: (
            <span className={`bold text-${iconText[sentiment]}`}>
              {showMinMaxLabel ? option.choice.label : option.choice.value + ' Stars'}
            </span>
          ),
          color: sentimentLabelColor[sentiment],
        }
      }
      return {
        content: (
          <span>
            {shouldShowChoiceValueOnRule ? (
              <>
                <i className={`text-${iconText[sentiment]} icon ${iconText[sentiment]}`} />
                <span className={`bold text-${iconText[sentiment]}`}>{option.choice.value}</span>
              </>
            ) : (
              <span className={`bold text-${iconText[sentiment]}`}>
                {option.choice.label || option.choice.value}
              </span>
            )}
          </span>
        ),
        color: sentimentLabelColor[sentiment],
      }
    } else {
      return {
        content: <span className="bold">{option.choice.label || option.choice.value}</span>,
      }
    }
  }

  function handleRuleChange(path: R.Path, value) {
    setQuestionCondition((state) => R.assocPath(path, value, state))
  }

  const filterRuleOptions = useCallback(
    (options, currentRules) =>
      options.filter(
        (option) =>
          !R.includes(option.value, selectedRuleOptions) || R.includes(option.value, currentRules)
      ),
    [selectedRuleOptions]
  )

  function calculateChoiceSentimentMapper(question): MapperType {
    const mapper = {
      positive: [],
      neutral: [],
      negative: [],
    }
    if (R.prop('is_opinion_scale', question)) {
      question.choices.forEach((choice) => {
        mapper[choice.sentiment] = [...mapper[choice.sentiment], choice.id]
      })
    }
    return mapper
  }

  function addLogic() {
    const conditionCount = questionCondition.conditions.length
    setForm(['conditions', conditionCount], DEFEAUT_CONDITION)
  }

  function handleTriggerSubmit() {
    if (R.prop('current', hiddenSubmitButtonRef)) {
      hiddenSubmitButtonRef.current.click()
    }
  }

  function handleSubmit(event) {
    event.stopPropagation()
    setIsTriggerSubmit(true)

    const newConditionsState = R.assocPath(
      ['conditions'],
      [
        ...questionCondition.conditions,
        ...deletedCondition.filter((condtion) => typeof condtion.id === 'number'),
      ],
      questionCondition
    )
    onSubmit(newConditionsState)
  }

  const noRuleOptions = useMemo(
    () => R.isEmpty(filterRuleOptions(ruleOptions, [])),
    [filterRuleOptions, ruleOptions]
  )

  const hasIncompleteRules = useMemo(
    () => questionCondition.conditions.some((condition) => R.isEmpty(condition.rules)),
    [questionCondition.conditions]
  )

  const isAddRuleDisabled = noRuleOptions || hasIncompleteRules

  const addRuleTooltip = noRuleOptions
    ? 'Cannot add more rules. All response options are already assigned.'
    : hasIncompleteRules
    ? 'Please complete all existing rules before adding a new one.'
    : ''

  return (
    <Modal open={isOpen} onClose={close}>
      <Modal.Header>Question actions</Modal.Header>
      <Modal.Content>
        <Form onSubmit={handleSubmit}>
          <input type="submit" hidden ref={hiddenSubmitButtonRef} />
          <div className="flex flex-col">
            <div className="field">
              <a
                href="https://help.simplesat.io/en/articles/2776705-survey-questions#h_59d547c946"
                target="_blank"
                rel="noreferrer"
              >
                <i className="icon far fa-book" />
                Learn more about question actions
              </a>
            </div>
            <div className="field">
              <Checkbox
                checked={questionCondition.is_active}
                className="mr-3"
                toggle
                data-testid="enable-follow-up-toggle"
                label="Enable question actions"
                onChange={(e, data) => setForm(['is_active'], data.checked)}
              />
            </div>
            {questionCondition.conditions.map((condition, conditionIndex) => (
              <div className="field" key={conditionIndex}>
                <StyledLogicSegment isActive={condition.is_active}>
                  <Grid>
                    {questionCondition.type === 'comment' ? (
                      <p className="font-bold">Triggers if any comment text is submitted.</p>
                    ) : questionCondition.type === 'file_upload' ? (
                      <p className="font-bold">Triggers if any file is uploaded.</p>
                    ) : (
                      <Grid.Row>
                        <Grid.Column
                          mobile={16}
                          tablet={4}
                          computer={4}
                          style={{ paddingBottom: '18px' }}
                        >
                          <label className="font-bold">Answer contains</label>
                        </Grid.Column>
                        <Grid.Column mobile={16} tablet={8} computer={8}>
                          <Dropdown
                            error={R.isEmpty(condition.rules) && isTriggerSubmit}
                            value={condition.rules}
                            index={conditionIndex}
                            placeholder="Select choice"
                            onChange={(e, { value }) =>
                              handleRuleChange(['conditions', conditionIndex, 'rules'], value)
                            }
                            selection
                            options={filterRuleOptions(ruleOptions, condition.rules)}
                            multiple
                            renderLabel={getLabel}
                            search
                            noResultsMessage="No more available choices"
                          />
                        </Grid.Column>

                        <ConfirmDeletePopup
                          onDelete={() => {
                            if (!R.isEmpty(condition.id)) {
                              const deletedCondition = R.assocPath(['is_active'], false, condition)
                              setdeletedConditions(
                                (state) => [...state, deletedCondition] as Condition[]
                              )
                            }
                            const newConditions = R.remove(
                              conditionIndex,
                              1,
                              questionCondition.conditions
                            )
                            setForm(['conditions'], newConditions)
                          }}
                          trigger={
                            <Button basic icon type="button" className="absolute right-0">
                              <i className="far fa-trash-can icon" />
                            </Button>
                          }
                          header="Delete rule?"
                          content="All conditions and details will be deleted."
                          buttonText="Delete rule"
                          position="bottom right"
                        />
                      </Grid.Row>
                    )}

                    <ConditionalSetting
                      key={conditionIndex}
                      logic={condition}
                      logicIndex={conditionIndex}
                      setForm={setForm}
                      isFileUploadPlanEnabled={isFileUploadPlanEnabled}
                    />
                  </Grid>
                </StyledLogicSegment>
              </div>
            ))}
            {questionCondition.type === 'comment' ||
            questionCondition.type === 'file_upload' ||
            questionCondition.type === 'multi_select_choice' ? null : isAddRuleDisabled ? (
              <Popup basic tooltip={addRuleTooltip}>
                <LinkButton type="button" disabled={true} className="self-start">
                  <i className="icon far fa-plus" />
                  Add rule
                </LinkButton>
              </Popup>
            ) : (
              <LinkButton type="button" onClick={addLogic} className="self-start">
                <i className="icon far fa-plus" />
                Add rule
              </LinkButton>
            )}
          </div>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button content="Cancel" onClick={close} type="button" />
        <AllowPermission permission={constants.permissions.manageSurveys}>
          <Button
            disabled={isLogicLoading}
            primary
            loading={isLogicLoading}
            content="Save"
            onClick={handleTriggerSubmit}
          />
        </AllowPermission>
      </Modal.Actions>
    </Modal>
  )
}

const StyledLogicSegment = styled(Segment.Group)`
  padding: 2rem 1rem !important;
  pointer-events: ${(props) => (props.isActive ? '' : 'none')};
  opacity: ${(props) => (props.isActive ? 1 : 0.5)};

  .ui.search.dropdown .menu {
    max-height: 300px;
  }
`

interface MapperType {
  positive: number[]
  neutral: number[]
  negative: number[]
}

export const iconText = {
  positive: 'green smile',
  neutral: 'yellow meh',
  negative: 'red frown',
}

const sentimentLabelColor = {
  positive: '!bg-green-lighter',
  neutral: '!bg-orange-lighter',
  negative: '!bg-red-lighter',
}

const DEFEAUT_CONDITION = {
  is_active: true,
  action: 'display_follow_up_question',
  rules: [],
  conditional_question_type: 'follow_up_comment_question',
  follow_up_comment_question: {
    id: null,
    metric: 'CUSTOM',
    text: '',
    type: 'comment',
    is_required: false,
    is_end_survey: false,
    choices: [],
  },
  follow_up_file_upload_question: {
    id: null,
    metric: 'CUSTOM',
    text: '',
    type: 'file_upload',
    is_required: false,
    is_end_survey: false,
    choices: [],
  },
  follow_up_multiple_choice_question: {
    id: null,
    metric: 'CUSTOM',
    text: '',
    type: 'multipleChoice',
    is_required: false,
    is_end_survey: false,
    choices: [
      {
        label: '',
        value: '',
        order: 1,
        is_active: true,
      },
      {
        label: '',
        value: '',
        order: 2,
        is_active: true,
      },
    ],
  },
  follow_up_multi_select_choice_question: {
    id: null,
    metric: 'CUSTOM',
    text: '',
    type: 'multi_select_choice',
    is_required: false,
    is_end_survey: false,
    choices: [
      {
        label: '',
        value: '',
        order: 1,
        is_active: true,
      },
      {
        label: '',
        value: '',
        order: 2,
        is_active: true,
      },
    ],
  },
}

type Choice = {
  id?: number
  is_active: boolean
  label: string
  value: string
  order: number
  sentiment?: string
}

type ConditionQuestion = {
  id?: number
  metric: 'CSAT' | 'NPS' | 'CES' | 'CUSTOM'
  text: string
  type: 'multipleChoice' | 'comment' | 'file_upload'
  is_required: boolean
  is_end_survey: boolean
  choices: Choice[]
}

type Condition = {
  id?: number
  is_active: boolean
  action: 'display_follow_up_question' | 'finish_survey'
  rules: number[] | string[]
  conditional_question_type:
    | 'follow_up_comment_question'
    | 'follow_up_file_upload_question'
    | 'follow_up_multiple_choice_question'
    | 'follow_up_multi_select_choice_question'
  follow_up_comment_question: ConditionQuestion
  follow_up_file_upload_question: ConditionQuestion
  follow_up_multiple_choice_question: ConditionQuestion
  follow_up_multi_select_choice_question: ConditionQuestion
}
type QuestionType = {
  is_active: boolean
  conditions: Condition[]
  choices: Choice[]
  metric: 'CSAT' | 'NPS' | 'CES' | 'CUSTOM'
  is_opinion_scale: boolean
  type: string
}

type QuestionConditionModalPropType = {
  question: QuestionType
  onSubmit: Function
  close: Function
  isOpen: boolean
  shouldShowChoiceValueOnRule: boolean
}



// WEBPACK FOOTER //
// ./src/organisms/QuestionConditionModal/index.tsx